import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsCanadaWebsite } from "@midland-industries/Components/WebsiteSelector";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonShapesRectangle,
    DynamicFormButtonTypes,
    DynamicFormButtonTypesSolid,
    DynamicFormButtonVariants,
    DynamicFormButtonVariantsPrimary,
    DynamicFormCheckboxGroupLabelPositionsRight,
    DynamicFormCheckboxLabelPositions,
    DynamicFormCheckboxLabelPositionsNone,
    DynamicFormCheckboxLabelPositionsRight,
    DynamicFormEmailRegExp,
    DynamicFormTextFieldLabelPositions,
    DynamicFormTextFieldLabelPositionsNone,
    DynamicFormThirdPartyApiTypes,
    DynamicFormThirdPartyApiTypesCustom,
} from "@midland-industries/Types/DynamicFormModels";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormCheckbox from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckbox";
import DynamicFormCheckboxGroup from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckboxGroup";
import DynamicFormSelect from "@midland-industries/Widgets/DynamicForms/DynamicFormSelect";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    successMessage = "successMessage",
    columns = "columns",
    enableRecaptcha = "enableRecaptcha",
    submitButtonText = "submitButtonText",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    endpointRequiresAuth = "endpointRequiresAuth",
    endpointUserId = "endpointUserId",
    endpointPassword = "endpointPassword",
    displayCustomApiResponse = "displayCustomApiResponse",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.successMessage]: string;
        [fields.columns]: number;
        [fields.enableRecaptcha]: boolean;
        [fields.submitButtonText]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.endpointRequiresAuth]: boolean;
        [fields.endpointUserId]: string;
        [fields.endpointPassword]: string;
        [fields.displayCustomApiResponse]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        isCanada: getIsCanadaWebsite(state),
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerApplicationFormFullStyles {
    wrapper?: InjectableCss;
    formContainer?: GridContainerProps;
    labelGridItem?: GridItemProps;
    fieldGridItem?: GridItemProps;
    fullWidthGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    termsLinkGridItem?: GridItemProps;
    termsLinkWrapper?: InjectableCss;
    termsAndConditionsLink?: LinkPresentationProps;
    labelWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    bodyTextWrapper?: InjectableCss;
    bodyText?: TypographyPresentationProps;
    bodyTextBold?: TypographyPresentationProps;
}

export const customerApplicationFormFullStyles: CustomerApplicationFormFullStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    formContainer: {
        gap: 0,
    },
    labelGridItem: {
        width: 3,
        css: css`
            align-items: flex-start;
            padding-bottom: 15px;
        `,
    },
    fieldGridItem: {
        width: 9,
        css: css`
            align-items: center;
            padding-bottom: 15px;
        `,
    },
    fullWidthGridItem: {
        width: 12,
        css: css`
            padding-bottom: 15px;
        `,
    },
    termsGridItem: {
        width: 3,
        css: css`
            align-items: center;
            padding-bottom: 15px;
        `,
    },
    termsLinkGridItem: {
        width: 6,
        css: css`
            align-items: center;
            padding-bottom: 15px;
        `,
    },
    labelWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    label: {
        css: css`
            font-weight: bold;
        `,
    },
    bodyTextWrapper: {},
    bodyText: {},
    bodyTextBold: {
        css: css`
            font-weight: bold;
        `,
    },
};

const styles = customerApplicationFormFullStyles;

const CustomerApplicationFormFull: FC<Props> = props => {
    const { id, fields, dynamicFormData, isCanada } = props;
    const { setDynamicFormFieldValue } = props;
    const {
        emailRecipients,
        successMessage,
        columns,
        enableRecaptcha,
        submitButtonText,
        buttonVariant,
        buttonShape,
        buttonType,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        endpointRequiresAuth,
        endpointUserId,
        endpointPassword,
        displayCustomApiResponse,
    } = fields;

    const formName = "Customer Application Form Full";

    const getDynamicFormIndex = () => {
        const formIndex = dynamicFormData.forms.findIndex(f => f.name === formName);
        return formIndex;
    };

    const getWidgetFromState = (formIndex: number, sourceKey: string) => {
        if (formIndex < 0) {
            return undefined;
        }
        const sourceIndex = dynamicFormData.forms[formIndex].fields.findIndex(f => f.key === sourceKey);
        if (sourceIndex < 0) {
            return undefined;
        }
        const sourceField = dynamicFormData.forms[formIndex].fields[sourceIndex];
        return sourceField;
    };

    const updateWidgetInState = (formIndex: number, sourceKey: string, targetKey: string) => {
        const sourceField = getWidgetFromState(formIndex, sourceKey);
        const targetField = getWidgetFromState(formIndex, targetKey);
        if (!sourceField) {
            return null;
        }
        if (!targetField) {
            return null;
        }
        setDynamicFormFieldValue({
            formName,
            formField: {
                key: targetKey,
                group: "",
                label: targetField.label,
                isRequired: targetField.isRequired,
                value: sourceField.value,
                type: targetField.type,
                sendOnlyIfChecked: false,
                hasError: sourceField.hasError,
                error: sourceField.error,
            },
        });
    };

    const handleCopyBillingClickEvent: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void = () => {
        const formIndex = getDynamicFormIndex();
        updateWidgetInState(formIndex, "address", "billing_address");
        updateWidgetInState(formIndex, "city", "billing_city");
        updateWidgetInState(formIndex, "state", "billing_state");
        updateWidgetInState(formIndex, "country", "billing_country");
        updateWidgetInState(formIndex, "postal_code", "billing_postal_code");
    };

    const handleCopyPurchasingClickEvent: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void =
        () => {
            const formIndex = getDynamicFormIndex();
            updateWidgetInState(formIndex, "full_name", "purchasing_name");
            updateWidgetInState(formIndex, "email", "purchasing_email");
            updateWidgetInState(formIndex, "address", "purchasing_address");
            updateWidgetInState(formIndex, "city", "purchasing_city");
            updateWidgetInState(formIndex, "state", "purchasing_state");
            updateWidgetInState(formIndex, "country", "purchasing_country");
            updateWidgetInState(formIndex, "postal_code", "purchasing_postal_code");
        };

    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicForm
                id={id}
                fields={{
                    formName,
                    emailRecipients,
                    submitButtonText,
                    successMessage,
                    buttonVariant,
                    buttonShape,
                    buttonType,
                    showResetButton: false,
                    enableRecaptcha,
                    sendToThirdPartyApi,
                    sendToApiType,
                    customApiEndpoint,
                    endpointRequiresAuth,
                    endpointUserId,
                    endpointPassword,
                    displayCustomApiResponse,
                }}
            >
                <GridContainer {...styles.formContainer}>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Company Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "company_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Company Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Doing Business As (DBA)")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "dba",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Doing Business As (DBA)",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Full Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "full_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Full Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: false,
                                maxLength: 0,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Email *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "email",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Email",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 254,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Address *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "address",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Address",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("City *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "city",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "City",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("State *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "state",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "State",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Country *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "country",
                                hasLabel: false,
                                label: "Country",
                                hintText: "",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "United States of America",
                                            value: "US",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Canada",
                                            value: "CA",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Mexico",
                                            value: "MX",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Postal Code *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "postal_code",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Postal Code",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}></Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormCheckbox
                            handleOnChange={handleCopyBillingClickEvent}
                            fields={{
                                key: "billing_same",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsRight,
                                label: "Billing Information Matches the Information Provided Above",
                                hintText: "",
                                isRequired: false,                                
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Billing Address *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "billing_address",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Billing Address",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Billing City *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "billing_city",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Billing City",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Billing State *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "billing_state",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Billing State",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Billing Country *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "billing_country",
                                hasLabel: false,
                                label: "Billing Country",
                                hintText: "",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "United States of America",
                                            value: "US",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Canada",
                                            value: "CA",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Mexico",
                                            value: "MX",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Billing Postal Code *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "billing_postal_code",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Billing Postal Code",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}></Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormCheckbox
                            handleOnChange={handleCopyPurchasingClickEvent}
                            fields={{
                                key: "purchasing_same",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsRight,
                                label: "Purchasing Information Matches the Information Provided Above",
                                hintText: "",
                                isRequired: false,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Email *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_email",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing Email",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 254,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Address *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_address",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing Address",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing City *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_city",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing City",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing State *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_state",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing State",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Country *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "purchasing_country",
                                hasLabel: false,
                                label: "Purchasing Country",
                                hintText: "",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "United States of America",
                                            value: "US",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Canada",
                                            value: "CA",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Mexico",
                                            value: "MX",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Purchasing Postal Code *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "purchasing_postal_code",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Purchasing Postal Code",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Phone *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "phone",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Phone",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: true,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Fax")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "fax",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Fax",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: true,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Accounting Contact *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "accounting_contact",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Accounting Contact",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Accounting Email *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "accounting_email",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Accounting Email",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 254,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Accounting Fax")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "accounting_fax",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Accounting Fax",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: true,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Industry *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "industry_id",
                                hasLabel: false,
                                label: "Industry",
                                hintText: "Select an Industry",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "Beverage",
                                            value: "22",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Car Wash",
                                            value: "23",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Compressor",
                                            value: "25",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Farm and Ag",
                                            value: "24",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Fastener",
                                            value: "16",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Fire Protection",
                                            value: "7",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Fluid Power",
                                            value: "15",
                                        },
                                    },
                                    { fields: { label: "HVAC", value: "36" } },
                                    {
                                        fields: {
                                            label: "Hardware/Retail",
                                            value: "35",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Hose and Fitting",
                                            value: "13",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Hydraulics",
                                            value: "17",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Industrial Supply",
                                            value: "14",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Irrigation",
                                            value: "4",
                                        },
                                    },
                                    { fields: { label: "LP Gas", value: "6" } },
                                    { fields: { label: "MRO", value: "29" } },
                                    {
                                        fields: {
                                            label: "Marine",
                                            value: "18",
                                        },
                                    },
                                    { fields: { label: "OEM", value: "20" } },
                                    {
                                        fields: {
                                            label: "Oil and Gas",
                                            value: "30",
                                        },
                                    },
                                    { fields: { label: "Other", value: "38" } },
                                    { fields: { label: "PVF", value: "2" } },
                                    {
                                        fields: {
                                            label: "Plumbing",
                                            value: "1",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Pneumatics",
                                            value: "34",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Power Transmission",
                                            value: "31",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Pressure Washer",
                                            value: "32",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "RV and Mobile Home",
                                            value: "12",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Refridgeration and Appliance",
                                            value: "5",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Swimming Pool",
                                            value: "11",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Truck and Auto",
                                            value: "19",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Utility",
                                            value: "10",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Water Filtration",
                                            value: "9",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Water Works",
                                            value: "37",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Waterwell",
                                            value: "8",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Primary Product Category *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "primary_product_category",
                                hasLabel: false,
                                label: "Primary Product Category",
                                hintText: "Select a Category",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "Brass Fittings",
                                            value: "Brass Fittings",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Hose Accessories",
                                            value: "Hose Accessories",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Hose Clamps",
                                            value: "Hose Clamps",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Hose",
                                            value: "Hose",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Hydraulics",
                                            value: "Hydraulics",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Lead Free",
                                            value: "Lead Free",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Pipes, Valves, and Fittings",
                                            value: "Pipe, Valves, and Fittings",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Plastic Fittings",
                                            value: "Plastic Fittings",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Plumbing Accessories",
                                            value: "Plumbing Accessories",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Pneumatic Firrings and Accessories",
                                            value: "Pneumatic Fittings and Accessories",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Stainless Steel Fittings and Nipples",
                                            value: "Stainless Steel Fittings and Nipples",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Tubing",
                                            value: "Tubing",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("How did you hear about us? *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "how_did_you_hear",
                                hasLabel: false,
                                label: "How did you hear about us?",
                                hintText: "Select an option",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "Referral",
                                            value: "Referral",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "SalesPerson",
                                            value: "SalesPerson",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Email",
                                            value: "Email",
                                        },
                                    },
                                    { fields: { label: "Web", value: "Web" } },
                                    {
                                        fields: {
                                            label: "Magazine",
                                            value: "Magazine",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Mailing",
                                            value: "Mailing",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Tradeshow",
                                            value: "Tradeshow",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Other",
                                            value: "Other",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{siteMessage("ReviewAndPay_UpsCollectLabel")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "ups_collect_account",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: isCanada ? "Collect Account" : "UPS Collect Account",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 100,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Backorders Accepted")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormCheckbox
                            fields={{
                                key: "backorders_accepted",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsNone,
                                label: "Backorders Accepted",
                                hintText: "",
                                isRequired: false,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>
                                {translate("Employer Identification Number (EIN) *")}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "ein",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Employer Identification Number (EIN)",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 50,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Business Type *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "business_type",
                                hasLabel: false,
                                label: "Business Type",
                                hintText: "Select a Business Type",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "Corporate",
                                            value: "Corporate",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Partnership",
                                            value: "Partnership",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "Proprietership",
                                            value: "Proprietership",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Reseller ID *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "reseller_id",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Reseller ID",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 50,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.fullWidthGridItem}>
                        <StyledWrapper {...styles.bodyTextWrapper}>
                            <Zone contentId={id} zoneName="customerApplicationFormFullZone1" />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Date Founded")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "date_founded",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Date Founded",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 50,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Annual Revenue *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormSelect
                            fields={{
                                key: "annual_revenue",
                                hasLabel: false,
                                label: "Annual Revenue",
                                hintText: "Select an option",
                                isRequired: true,
                                options: [
                                    {
                                        fields: {
                                            label: "Under $1M",
                                            value: "Under $1M",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "1-5M",
                                            value: "1-5M",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "5-10M",
                                            value: "5-10M",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "10-25M",
                                            value: "10-25M",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "25-50M",
                                            value: "25-50M",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "50-100M",
                                            value: "50-100M",
                                        },
                                    },
                                    {
                                        fields: {
                                            label: "100M+",
                                            value: "100M+",
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Bank Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "bank_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Bank Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Bank Contact *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "bank_contact",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Bank Contact",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 200,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Bank Phone *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "bank_phone",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Bank Phone",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 30,
                                validateAsPhoneNumber: true,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Principal Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "principal_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Principal Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 100,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Pricipal Title *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "principal_title",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Pricipal Title",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 100,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Principal Residence Address")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "principal_residence_address",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Principal Residence Address",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("First Vendor Reference Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "first_vendor_reference_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "First Vendor Reference Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("First Vendor Reference Phone *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "first_vendor_reference_phone",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "First Vendor Reference Phone",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: true,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Second Vendor Reference Name *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "second_vendor_reference_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Second Vendor Reference Name",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Second Vendor Reference Phone *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "second_vendor_reference_phone",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Second Vendor Reference Phone",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: true,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>
                                {translate("What Buying Groups do you belong to? *")}
                            </Typography>
                            <br />
                            <Typography>{translate("Please select all that apply.")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormCheckboxGroup
                            fields={{
                                key: "buying_groups[]",
                                hasLabel: false,
                                label: "What Buying Groups do you belong to?",
                                columns: 2,
                                requireOneSelection: true,
                                sendOnlyIfChecked: true,
                                topics: [
                                    {
                                        fields: {
                                            key: "1",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "AD",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "2",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IDCO",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "3",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IDC",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "4",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "Net Plus Alliance",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "5",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "DPA",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "6",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IBC",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "10",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "HDA",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "11",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "VIPAR",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "13",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "IMARK",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "14",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "TDG",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "15",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "HARDI",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                    {
                                        fields: {
                                            key: "16",
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: "None",
                                            changeTrueFalseToYesNo: true,
                                        },
                                    },
                                ],
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Any Other Buying Groups")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "buying_groups_other",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Any Other Buying Groups",
                                hintText: "",
                                isRequired: false,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: false,
                                maxLength: 100,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Electronic Signature *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <DynamicFormTextField
                            fields={{
                                key: "applicant_name",
                                labelPosition: DynamicFormTextFieldLabelPositionsNone,
                                label: "Electronic Signature",
                                hintText: "",
                                isRequired: true,
                                enableMinLength: false,
                                minLength: 0,
                                enableMaxLength: true,
                                maxLength: 300,
                                validateAsPhoneNumber: false,
                                validateAsEmailAddress: false,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <StyledWrapper {...styles.labelWrapper}>
                            <Typography {...styles.label}>{translate("Accept Terms *")}</Typography>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.termsGridItem}>
                        <DynamicFormCheckbox
                            fields={{
                                key: "accept_terms",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsNone,
                                label: "Accept Terms",
                                hintText: "",
                                isRequired: true,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: true,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.termsLinkGridItem}>
                        <StyledWrapper {...styles.termsLinkWrapper}>
                            <Link {...styles.termsAndConditionsLink} href="/terms-and-conditions" target="_blank">
                                Terms and Conditions
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
                <StyledWrapper {...styles.bodyTextWrapper}>
                    <Zone contentId={id} zoneName="customerApplicationFormFullZone2" />
                </StyledWrapper>
            </DynamicForm>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const actionTab = {
    displayName: "Actions",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerApplicationFormFull),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 1,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 2,
            },
            {
                name: fields.columns,
                displayName: "Columns",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isRequired: true,
                min: 1,
                max: 3,
                tooltip: "How many columns to use to display the checkbox group. Min 1. Max 3.",
                sortOrder: 3,
                tab: formTab,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 100,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: "primary",
                    },
                    {
                        displayName: "Secondary",
                        value: "secondary",
                    },
                    {
                        displayName: "Tertiary",
                        value: "tertiary",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 105,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: "rectangle",
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: "rectangle",
                    },
                    {
                        displayName: "Pill",
                        value: "pill",
                    },
                    {
                        displayName: "Rounded",
                        value: "rounded",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: "solid",
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: "solid",
                    },
                    {
                        displayName: "Outline",
                        value: "outline",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 115,
            },
            {
                name: fields.sendToThirdPartyApi,
                displayName: "Upload FormData to 3rd Party System",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 200,
                tab: actionTab,
            },
            {
                name: fields.sendToApiType,
                displayName: "Select 3rd Party System Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormThirdPartyApiTypesCustom,
                fieldType: "General",
                options: [
                    {
                        displayName: "Custom API Endpoint",
                        value: DynamicFormThirdPartyApiTypesCustom,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 210,
            },
            {
                name: fields.customApiEndpoint,
                displayName: "Custom API Endpoint URL",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi && widget.fields.sendToApiType === "custom-api",
                tab: actionTab,
                sortOrder: 220,
            },
            {
                name: fields.endpointRequiresAuth,
                displayName: "Upload Requires Authentication",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 221,
            },
            {
                name: fields.endpointUserId,
                displayName: "User Id",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 222,
            },
            {
                name: fields.endpointPassword,
                displayName: "Password",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 223,
            },
            {
                name: fields.displayCustomApiResponse,
                displayName: "Display results from API below Form for testing",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 230,
                tab: actionTab,
            },
        ],
    },
};

export default widgetModule;
